/**
 * Insertable Streams utilities.
 *
 * Note: Audio tracks no longer embed metadata - animation data is now sent via a separate video track.
 * These utilities are kept for potential future use and backwards compatibility.
 *
 * @internal
 * @packageDocumentation
 */

/**
 * Check if browser supports RTCRtpScriptTransform (the modern Insertable Streams API).
 * @returns true if RTCRtpScriptTransform is available
 * @internal
 */
export function supportsScriptTransform(): boolean {
  return typeof RTCRtpScriptTransform !== 'undefined';
}

/**
 * Check if browser supports createEncodedStreams (older Insertable Streams API).
 * @returns true if createEncodedStreams is available
 * @internal
 */
export function supportsEncodedStreams(): boolean {
  // This is deprecated but still works in some browsers
  // @ts-expect-error - experimental API
  return typeof RTCRtpSender?.prototype?.createEncodedStreams === 'function';
}

/**
 * Insertable Streams method type.
 * @internal
 */
export type InsertableStreamsMethod =
  | 'scriptTransform'
  | 'encodedStreams'
  | null;

/**
 * Get supported Insertable Streams method.
 * @returns The supported method or null if none available
 * @internal
 */
export function getInsertableStreamsMethod(): InsertableStreamsMethod {
  if (supportsScriptTransform()) return 'scriptTransform';
  if (supportsEncodedStreams()) return 'encodedStreams';
  return null;
}
